import React, { createContext, useContext, ReactNode, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUserPreferences } from "../api/queries";
import { updateUserPreferences } from "../api/client";
import { UserPreferences } from "../types";

interface VideoPreferencesContextType {
  videoPlaybackSpeed: number;
  setVideoPlaybackSpeed: (speed: number) => Promise<void>;
  autoplayVideos: boolean;
  setAutoplayVideos: (autoplay: boolean) => Promise<void>;
  videoVolume: number;
  setVideoVolume: (volume: number) => Promise<void>;
  isLoading: boolean;
  isUpdatingAutoplay: boolean;
  isUpdatingSpeed: boolean;
  isUpdatingVolume: boolean;
}

const VideoPreferencesContext = createContext<
  VideoPreferencesContextType | undefined
>(undefined);

export const useVideoPreferences = () => {
  const context = useContext(VideoPreferencesContext);
  if (!context) {
    throw new Error(
      "useVideoPreferences must be used within a VideoPreferencesProvider",
    );
  }
  return context;
};

interface VideoPreferencesProviderProps {
  children: ReactNode;
}

export const VideoPreferencesProvider: React.FC<
  VideoPreferencesProviderProps
> = ({ children }) => {
  const queryClient = useQueryClient();
  const { data: userPreferences, isLoading } = useGetUserPreferences();
  const [isUpdatingAutoplay, setIsUpdatingAutoplay] = useState(false);
  const [isUpdatingSpeed, setIsUpdatingSpeed] = useState(false);
  const [isUpdatingVolume, setIsUpdatingVolume] = useState(false);

  const videoPlaybackSpeed = userPreferences?.videoPlaybackSpeed ?? 1;
  const autoplayVideos = userPreferences?.autoplayVideos ?? false;
  const videoVolume = userPreferences?.videoVolume ?? 1;

  const setVideoPlaybackSpeed = async (speed: number) => {
    // Validate speed is within range
    if (speed < 0.25 || speed > 2) {
      throw new Error("Video playback speed must be between 0.25 and 2");
    }

    setIsUpdatingSpeed(true);
    try {
      // Update preferences on the server
      // Use userPreferences?.field to avoid overwriting with defaults during race conditions
      await updateUserPreferences(
        userPreferences?.saveAnswersToLog ?? true,
        userPreferences?.askEveryTime,
        userPreferences?.emailAccountIssues,
        userPreferences?.emailFeedbackRequests,
        userPreferences?.emailNewsUpdates,
        speed,
        userPreferences?.autoplayVideos,
        userPreferences?.videoVolume,
      );

      // Invalidate the query to refetch updated preferences
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    } finally {
      setIsUpdatingSpeed(false);
    }
  };

  const setAutoplayVideos = async (autoplay: boolean) => {
    setIsUpdatingAutoplay(true);
    try {
      // Update preferences on the server
      // Use userPreferences?.field to avoid overwriting with defaults during race conditions
      await updateUserPreferences(
        userPreferences?.saveAnswersToLog ?? true,
        userPreferences?.askEveryTime,
        userPreferences?.emailAccountIssues,
        userPreferences?.emailFeedbackRequests,
        userPreferences?.emailNewsUpdates,
        userPreferences?.videoPlaybackSpeed,
        autoplay,
        userPreferences?.videoVolume,
      );

      // Invalidate the query to refetch updated preferences
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    } finally {
      setIsUpdatingAutoplay(false);
    }
  };

  const setVideoVolume = async (volume: number) => {
    // Validate volume is within range
    if (volume < 0 || volume > 1) {
      throw new Error("Video volume must be between 0 and 1");
    }

    setIsUpdatingVolume(true);
    try {
      // Optimistically update the cache immediately so new videos get the correct volume
      queryClient.setQueryData(
        ["userPreferences"],
        (old: UserPreferences | undefined) => ({
          ...old,
          videoVolume: volume,
        }),
      );

      // Update preferences on the server
      // Use userPreferences?.field to avoid overwriting with defaults during race conditions
      await updateUserPreferences(
        userPreferences?.saveAnswersToLog ?? true,
        userPreferences?.askEveryTime,
        userPreferences?.emailAccountIssues,
        userPreferences?.emailFeedbackRequests,
        userPreferences?.emailNewsUpdates,
        userPreferences?.videoPlaybackSpeed,
        userPreferences?.autoplayVideos,
        volume,
      );

      // No need to invalidate - we already updated optimistically
      // Invalidating causes race conditions with rapid volume changes
      // The optimistic update is the source of truth until next natural refetch
    } finally {
      setIsUpdatingVolume(false);
    }
  };

  return (
    <VideoPreferencesContext.Provider
      value={{
        videoPlaybackSpeed,
        setVideoPlaybackSpeed,
        autoplayVideos,
        setAutoplayVideos,
        videoVolume,
        setVideoVolume,
        isLoading,
        isUpdatingAutoplay,
        isUpdatingSpeed,
        isUpdatingVolume,
      }}
    >
      {children}
    </VideoPreferencesContext.Provider>
  );
};
